import type { Config } from "tailwindcss";

import spacingData from "./config/spacing";
import avantiColorMap from "./config/tailwind-colors/avanti-color-map";
import expatColorMap from "./config/tailwind-colors/expat-color-map";
import stsColorMap from "./config/tailwind-colors/sts-color-map";
import avnFont from "./config/tailwind-font/avnti-font";
import expatFont from "./config/tailwind-font/expact-font";
import stsFont from "./config/tailwind-font/sts-font";
import { PRODUCT, SECONDARY_PRODUCT } from "./constants";

const colors = {
  avanti: avantiColorMap,
  staysure: stsColorMap,
  expat: expatColorMap,
};

const fonts = {
  avanti: avnFont,
  staysure: stsFont,
  expat: expatFont,
};

const headerFontWeight = {
  avanti: {
    h1: "700",
    h2: "700",
  },
  staysure: {
    h1: "400",
    h2: "600",
  },
  expat: {
    h1: "400",
    h2: "600",
  },
};

const headerFontSize = {
  avanti: {
    md: "23px",
    sm: "23px",
  },
  staysure: {
    md: "25px",
    sm: "20px",
  },
  expat: {
    md: "25px",
    sm: "20px",
  },
};

const wrapperWidth = {
  avanti: {
    sm: "280px",
    md: "440px",
    lg: "530px",
  },
  staysure: {
    sm: "280px",
    md: "528px",
    lg: "444px",
  },
  expat: {
    sm: "280px",
    md: "528px",
    lg: "444px",
  },
};

const headerLineHeight = {
  avanti: {
    xs: "18px",
    sm: "23px",
    md: "26px",
    lg: "27px",
    xl: "34px",
    xxl: "28px",
    "l/xl": "30px",
    compact: "26px",
  },
  staysure: {
    xs: "18px",
    sm: "21px",
    md: "21px",
    lg: "24px",
    "l/xl": "30px",
    xl: "34px",
    xxl: "27px",
    compact: "28px",
  },
  expat: {
    xs: "18px",
    sm: "21px",
    md: "21px",
    lg: "24px",
    xl: "34px",
    xxl: "27px",
    "l/xl": "30px",
    compact: "28px",
  },
};

const baseFontSize = {
  avanti: "18px",
  staysure: "16px",
  expat: "16px",
};

const fontSemibold = {
  avanti: { default: "700", large: "700" },
  staysure: { default: "500", large: "600" },
  expat: { default: "500", large: "600" },
};

export const brand: "staysure" | "avanti" | "expat" =
  SECONDARY_PRODUCT === "expat"
    ? "expat"
    : (PRODUCT as "staysure" | "avanti") ?? "staysure";

const selectedColors = colors[brand];
const selectedFonts = fonts[brand];
const headerFontWeightValue = headerFontWeight[brand];
const fontSemiboldValue = fontSemibold[brand];

const config: Config = {
  content: [
    "./pages/**/*.{js,ts,jsx,tsx,mdx}",
    "./components/**/*.{js,ts,jsx,tsx,mdx}",
    "./app/**/*.{js,ts,jsx,tsx,mdx}",
    "./node_modules/medical-screening-lib/**/*.js",
  ],
  theme: {
    screens: {
      sm: "360px",
      // => @media (min-width: 360px) { ... }

      md: "600px",
      // => @media (min-width: 600px) { ... }

      tablet: "768px",
      // => @media (min-width: 768px) { ... }

      lg: "904px",
      // => @media (min-width: 904px) { ... }

      smallDesktop: "1024px",
      // => @media (min-width: 1024px) { ... }

      xl: "1440px",
      // => @media (min-width: 1440px) { ... }
    },
    // fontSize: {
    //   xs: "10px",
    //   base: "16px",
    //   lg: "18px",
    //   xl: "25px",
    //   "2xl": "31px",
    //   "4xl": "61px",
    // },
    fontSize: {
      "3xs": "14px",
      "4xs": "12px",
      "2xs": "16px",
      xs: "18px",
      s: "20px",
      m: "23px",
      l: "25px",
      xl: "26px",
      "2xl": "29px",
      "3xl": "31px",
      "4xl": "32px",
      "5xl": "39px",
      "6xl": "49px",
      "7xl": "61px",
    },

    extend: {
      fontFamily: selectedFonts,
      fontWeight: {
        "header-font": headerFontWeightValue.h1,
        "sub-header-font": headerFontWeightValue.h2,
        "font-semibold": fontSemiboldValue.default,
        "font-semibold-large": fontSemiboldValue.large,
      },
      fontSize: {
        xls: "23px",
        "base-font-size": baseFontSize[brand],
        "header-font-size-medium": headerFontSize[brand].md,
        "header-font-size-small": headerFontSize[brand].sm,
      },
      colors: selectedColors,
      spacing: {
        "grid-system-space-025": spacingData.spacing["grid-system-space-025"],
        "grid-system-space-050": spacingData.spacing["grid-system-space-050"],
        "grid-system-space-075": spacingData.spacing["grid-system-space-075"],
        "grid-system-space-100": spacingData.spacing["grid-system-space-100"],
        "grid-system-space-150": spacingData.spacing["grid-system-space-150"],
        "grid-system-space-200": spacingData.spacing["grid-system-space-200"],
        "grid-system-space-300": spacingData.spacing["grid-system-space-300"],
        "grid-system-space-400": spacingData.spacing["grid-system-space-400"],
        "grid-system-space-500": spacingData.spacing["grid-system-space-500"],
        "grid-system-space-600": spacingData.spacing["grid-system-space-600"],
        "grid-system-space-700": spacingData.spacing["grid-system-space-700"],
        "grid-system-space-800": spacingData.spacing["grid-system-space-800"],
        "grid-system-space-900": spacingData.spacing["grid-system-space-900"],
        "grid-system-space-1000": spacingData.spacing["grid-system-space-1000"],
        "grid-system-space-1200": spacingData.spacing["grid-system-space-1200"],
        "grid-system-space-1500": spacingData.spacing["grid-system-space-1500"],

        "spacing-xxs": spacingData.spacing["grid-system-space-050"],
        "spacing-xs": spacingData.spacing["grid-system-space-100"],
        "spacing-s": spacingData.spacing["grid-system-space-150"],
        "spacing-m": spacingData.spacing["grid-system-space-200"],
        "spacing-l": spacingData.spacing["grid-system-space-300"],
        "spacing-xl": spacingData.spacing["grid-system-space-400"],
        "spacing-xxl": spacingData.spacing["grid-system-space-600"],
        "spacing-xxxl": spacingData.spacing["grid-system-space-700"],
        "spacing-4xl": spacingData.spacing["grid-system-space-800"],
        "spacing-5xl": spacingData.spacing["grid-system-space-1000"],
        "spacing-6xl": spacingData.spacing["grid-system-space-1500"],

        "compact-spacing-xxs": spacingData.spacing["grid-system-space-025"],
        "compact-spacing-xs": spacingData.spacing["grid-system-space-050"],
        "compact-spacing-s": spacingData.spacing["grid-system-space-100"],
        "compact-spacing-m": spacingData.spacing["grid-system-space-150"],
        "compact-spacing-l": spacingData.spacing["grid-system-space-200"],
        "compact-spacing-xl": spacingData.spacing["grid-system-space-300"],
        "compact-spacing-xxl": spacingData.spacing["grid-system-space-500"],
        "compact-spacing-xxxl": spacingData.spacing["grid-system-space-600"],
        "compact-spacing-4xl": spacingData.spacing["grid-system-space-700"],
        "compact-spacing-5xl": spacingData.spacing["grid-system-space-800"],
        "compact-spacing-6xl": spacingData.spacing["grid-system-space-1200"],
      },
      borderRadius: {
        "radius-s": spacingData.spacing["grid-system-space-050"],
        "radius-m": spacingData.spacing["grid-system-space-075"],
        "radius-lg": spacingData.spacing["grid-system-space-100"],
        "radius-xl": spacingData.spacing["grid-system-space-150"],
        "radius-xxl": spacingData.spacing["grid-system-space-200"],
        "radius-pill": spacingData.spacing["grid-system-space-1000"],
        "radius-full": spacingData.spacing["grid-system-space-1500"],
        "compact-radius-s": spacingData.spacing["grid-system-space-050"],
        "compact-radius-m": spacingData.spacing["grid-system-space-075"],
        "compact-radius-lg": spacingData.spacing["grid-system-space-100"],
        "compact-radius-xl": spacingData.spacing["grid-system-space-150"],
        "compact-radius-xxl": spacingData.spacing["grid-system-space-200"],
        "compact-radius-pill": spacingData.spacing["grid-system-space-1000"],
      },
      height: {
        "size-xs": spacingData.spacing["grid-system-space-200"],
        "size-s": spacingData.spacing["grid-system-space-300"],
        "size-m": spacingData.spacing["grid-system-space-400"],
        "size-l": spacingData.spacing["grid-system-space-500"],
        "size-xl": spacingData.spacing["grid-system-space-600"],
        "size-xxl": spacingData.spacing["grid-system-space-700"],
        "size-xxxl": spacingData.spacing["grid-system-space-800"],
        "compact-size-xs": spacingData.spacing["grid-system-space-150"],
        "compact-size-s": spacingData.spacing["grid-system-space-200"],
        "compact-size-m": spacingData.spacing["grid-system-space-300"],
        "compact-size-l": spacingData.spacing["grid-system-space-400"],
        "compact-size-xl": spacingData.spacing["grid-system-space-500"],
        "compact-size-xxl": spacingData.spacing["grid-system-space-600"],
        "compact-size-xxxl": spacingData.spacing["grid-system-space-700"],
      },
      gap: {
        "largeDesktop-gutter": spacingData.spacing["grid-system-space-300"],
        "smallDesktop-gutter": spacingData.spacing["grid-system-space-300"],
        "tablet-gutter": spacingData.spacing["grid-system-space-200"],
      },
      margin: {
        "largeDesktop-margin": spacingData.spacing["grid-system-space-1500"],
        "smallDesktop-margin": spacingData.spacing["grid-system-space-900"],
        "tablet-margin": spacingData.spacing["grid-system-space-600"],
      },
      padding: {
        "largeDesktop-vertical-padding":
          spacingData.spacing["grid-system-space-600"],
        "smallDesktop-vertical-padding":
          spacingData.spacing["grid-system-space-600"],
        "tablet-vertical-padding": spacingData.spacing["grid-system-space-400"],
      },
      lineHeight: {
        "custom-size-xs": headerLineHeight[brand].xs,
        "custom-size-sm": headerLineHeight[brand].sm,
        "custom-size-md": headerLineHeight[brand].md,
        "custom-size-lg": headerLineHeight[brand].lg,
        "custom-size-xl": headerLineHeight[brand].xl,
        "custom-size-xxl": headerLineHeight[brand].xxl,
        "custom-size-l/xl": headerLineHeight[brand]["l/xl"],
        "custom-size-compact": headerLineHeight[brand].compact,
      },
      maxWidth: {
        "custom-wrappersize-sm": wrapperWidth[brand].sm,
        "custom-wrappersize-md": wrapperWidth[brand].md,
        "custom-wrappersize-lg": wrapperWidth[brand].lg,
      },
      keyframes: {
        loading: {
          "0%": {
            "background-position-x": "250%",
          },
          "100%": {
            "background-position-x": "-50%",
          },
        },
      },
      animation: {
        pulse: "pulse 2s ease-in-out infinite",
        loading: "4100ms loading linear infinite 1200ms",
      },
    },
    plugins: [],
  },
};
export default config;
